import React, { createContext, useContext, useEffect, useState } from "react";
import client from "../api/client";

export interface AuthUser {
  id: number;
  username: string;
  createdAt: string;
}

interface AuthContextProps {
  currentUser: AuthUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps>({
  currentUser: null,
  loading: true,
  login: async () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    client
      .get("/staff-auth/me")
      .then(({ data }) => setCurrentUser({ id: data.id, username: data.username, createdAt: data.createdAt }))
      .catch(() => {
        localStorage.removeItem("token");
        setCurrentUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (username: string, password: string) => {
    const { data } = await client.post("/staff-auth/login", { username, password });
    localStorage.setItem("token", data.token);
    setCurrentUser({ id: data.staffId, username, createdAt: "" });
  };

  const logout = () => {
    localStorage.removeItem("token");
    setCurrentUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ currentUser, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
